/**
 * Structured Production-results PDF: canvas charts + text tables.
 * Charts are never sliced across pages (unlike html2canvas of the live UI).
 */
import { jsPDF } from 'jspdf';

import { FIELD_COLORS, niceCeil } from '@/components/Scenarios/FieldProductionChart';
import type { MbFieldAggregate, MbFieldSeriesPoint } from '@/utils/materialBalanceFieldAggregate';
import type { LayerPassportViewModel, PassportKv } from '@/utils/materialBalanceLayerPassport';
import type { MaterialBalanceFieldPoint } from '@/types/materialBalance';
import type { RelPermChartPoint } from '@/utils/materialBalanceRelperm';

const MARGIN = 14;
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN * 2;

type Series = {
  key: string;
  label: string;
  color: string;
  dash?: number[];
  /** Which Y scale: left | wc (0–100) | right */
  scale: 'left' | 'wc' | 'right';
  values: Array<number | null>;
};

function fmtTick(v: number): string {
  if (!Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  if (abs >= 100 || Number.isInteger(v)) return String(Math.round(v));
  if (abs >= 10) return (Math.round(v * 10) / 10).toString();
  return (Math.round(v * 100) / 100).toString();
}

function maxOf(vals: Array<number | null | undefined>): number {
  let m = 0;
  for (const v of vals) {
    if (v != null && Number.isFinite(v)) m = Math.max(m, v);
  }
  return m;
}

/** High-DPI canvas line chart → PNG data URL. */
function renderLineChartPng(opts: {
  widthPx?: number;
  heightPx?: number;
  years: number[];
  series: Series[];
  leftMax?: number;
  rightMax?: number;
  title?: string;
}): string {
  const width = opts.widthPx ?? 1400;
  const height = opts.heightPx ?? 720;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const pad = { top: 48, right: 120, bottom: 56, left: 64 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  if (opts.title) {
    ctx.fillStyle = '#212121';
    ctx.font = '600 28px system-ui, sans-serif';
    ctx.fillText(opts.title, pad.left, 32);
  }

  const years = opts.years;
  if (!years.length) {
    ctx.fillStyle = '#757575';
    ctx.font = '20px system-ui, sans-serif';
    ctx.fillText('No series', pad.left, pad.top + 40);
    return canvas.toDataURL('image/png');
  }

  const xMin = years[0];
  const xMax = years[years.length - 1];
  const xSpan = Math.max(xMax - xMin, 1e-9);

  const leftSeries = opts.series.filter((s) => s.scale === 'left');
  const rightSeries = opts.series.filter((s) => s.scale === 'right');
  const wcSeries = opts.series.filter((s) => s.scale === 'wc');

  const leftMax = niceCeil(
    (opts.leftMax ?? maxOf(leftSeries.flatMap((s) => s.values))) * 1.08 || 1,
  );
  const rightMax = niceCeil(
    (opts.rightMax ?? maxOf(rightSeries.flatMap((s) => s.values))) * 1.08 || 1,
  );
  const wcMax = 100;

  const xAt = (year: number) => pad.left + ((year - xMin) / xSpan) * plotW;
  const yLeft = (v: number) => pad.top + plotH * (1 - v / leftMax);
  const yRight = (v: number) => pad.top + plotH * (1 - v / rightMax);
  const yWc = (v: number) => pad.top + plotH * (1 - v / wcMax);

  // Grid
  ctx.strokeStyle = '#e0e0e0';
  ctx.lineWidth = 1;
  for (let i = 0; i <= 5; i++) {
    const y = pad.top + (plotH * i) / 5;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + plotW, y);
    ctx.stroke();
  }

  // Plot frame
  ctx.strokeStyle = '#9e9e9e';
  ctx.strokeRect(pad.left, pad.top, plotW, plotH);

  // Left ticks
  ctx.fillStyle = '#455a64';
  ctx.font = '18px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (let i = 0; i <= 5; i++) {
    const v = (leftMax * (5 - i)) / 5;
    const y = pad.top + (plotH * i) / 5;
    ctx.fillText(fmtTick(v), pad.left - 10, y);
  }
  ctx.save();
  ctx.translate(18, pad.top + plotH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.textAlign = 'center';
  ctx.fillText('rates / injection', 0, 0);
  ctx.restore();

  // WC ticks (inner right)
  let rightBandX = pad.left + plotW + 8;
  if (wcSeries.length) {
    ctx.fillStyle = FIELD_COLORS.waterCut;
    ctx.textAlign = 'left';
    for (let i = 0; i <= 5; i++) {
      const v = (wcMax * (5 - i)) / 5;
      const y = pad.top + (plotH * i) / 5;
      ctx.fillText(fmtTick(v), rightBandX, y);
    }
    ctx.strokeStyle = FIELD_COLORS.waterCut;
    ctx.beginPath();
    ctx.moveTo(pad.left + plotW, pad.top);
    ctx.lineTo(pad.left + plotW, pad.top + plotH);
    ctx.stroke();
    rightBandX += 44;
  }

  // Np ticks (outer right)
  if (rightSeries.length) {
    ctx.fillStyle = FIELD_COLORS.np;
    ctx.textAlign = 'left';
    for (let i = 0; i <= 5; i++) {
      const v = (rightMax * (5 - i)) / 5;
      const y = pad.top + (plotH * i) / 5;
      ctx.fillText(fmtTick(v), rightBandX, y);
    }
    ctx.strokeStyle = FIELD_COLORS.np;
    ctx.beginPath();
    ctx.moveTo(rightBandX - 4, pad.top);
    ctx.lineTo(rightBandX - 4, pad.top + plotH);
    ctx.stroke();
  }

  // X ticks
  ctx.fillStyle = '#455a64';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const xTicks = 6;
  for (let i = 0; i <= xTicks; i++) {
    const year = xMin + (xSpan * i) / xTicks;
    ctx.fillText(String(Math.round(year)), xAt(year), pad.top + plotH + 10);
  }
  ctx.fillText('year', pad.left + plotW / 2, pad.top + plotH + 34);

  const drawSeries = (s: Series, yFn: (v: number) => number) => {
    ctx.strokeStyle = s.color;
    ctx.lineWidth = 2.5;
    if (s.dash) ctx.setLineDash(s.dash);
    else ctx.setLineDash([]);
    ctx.beginPath();
    let started = false;
    for (let i = 0; i < years.length; i++) {
      const v = s.values[i];
      if (v == null || !Number.isFinite(v)) {
        started = false;
        continue;
      }
      const x = xAt(years[i]);
      const y = yFn(v);
      if (!started) {
        ctx.moveTo(x, y);
        started = true;
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
    ctx.setLineDash([]);
  };

  for (const s of leftSeries) drawSeries(s, yLeft);
  for (const s of wcSeries) drawSeries(s, yWc);
  for (const s of rightSeries) drawSeries(s, yRight);

  // Legend
  let lx = pad.left;
  const ly = height - 18;
  ctx.font = '16px system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  for (const s of opts.series) {
    ctx.strokeStyle = s.color;
    ctx.lineWidth = 3;
    if (s.dash) ctx.setLineDash(s.dash);
    else ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(lx, ly);
    ctx.lineTo(lx + 22, ly);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#424242';
    ctx.fillText(s.label, lx + 28, ly);
    lx += 28 + ctx.measureText(s.label).width + 22;
  }

  return canvas.toDataURL('image/png');
}

function renderBarsPng(opts: {
  title: string;
  years: number[];
  producers: number[];
  injectors: number[];
}): string {
  const width = 1400;
  const height = 560;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const pad = { top: 48, right: 24, bottom: 56, left: 64 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#212121';
  ctx.font = '600 28px system-ui, sans-serif';
  ctx.fillText(opts.title, pad.left, 32);

  const n = opts.years.length;
  if (!n) return canvas.toDataURL('image/png');

  const yMax = niceCeil(maxOf([...opts.producers, ...opts.injectors]) * 1.15 || 1);
  const groupW = plotW / n;
  const barW = Math.max(4, groupW * 0.35);

  ctx.strokeStyle = '#e0e0e0';
  for (let i = 0; i <= 5; i++) {
    const y = pad.top + (plotH * i) / 5;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + plotW, y);
    ctx.stroke();
  }
  ctx.strokeStyle = '#9e9e9e';
  ctx.strokeRect(pad.left, pad.top, plotW, plotH);

  ctx.fillStyle = '#455a64';
  ctx.font = '18px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (let i = 0; i <= 5; i++) {
    const v = (yMax * (5 - i)) / 5;
    ctx.fillText(fmtTick(v), pad.left - 10, pad.top + (plotH * i) / 5);
  }

  for (let i = 0; i < n; i++) {
    const cx = pad.left + groupW * (i + 0.5);
    const pH = (opts.producers[i] / yMax) * plotH;
    const iH = (opts.injectors[i] / yMax) * plotH;
    ctx.fillStyle = FIELD_COLORS.prodWells;
    ctx.fillRect(cx - barW - 2, pad.top + plotH - pH, barW, pH);
    ctx.fillStyle = FIELD_COLORS.injWells;
    ctx.fillRect(cx + 2, pad.top + plotH - iH, barW, iH);
  }

  ctx.fillStyle = '#455a64';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const step = Math.max(1, Math.floor(n / 8));
  for (let i = 0; i < n; i += step) {
    const cx = pad.left + groupW * (i + 0.5);
    ctx.fillText(String(opts.years[i]), cx, pad.top + plotH + 10);
  }

  // Legend
  ctx.fillStyle = FIELD_COLORS.prodWells;
  ctx.fillRect(pad.left, height - 28, 16, 12);
  ctx.fillStyle = '#424242';
  ctx.font = '16px system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('producers', pad.left + 22, height - 22);
  ctx.fillStyle = FIELD_COLORS.injWells;
  ctx.fillRect(pad.left + 120, height - 28, 16, 12);
  ctx.fillStyle = '#424242';
  ctx.fillText('injectors', pad.left + 142, height - 22);

  return canvas.toDataURL('image/png');
}

function renderRelpermPng(pts: RelPermChartPoint[], title: string): string {
  const width = 1000;
  const height = 480;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const pad = { top: 48, right: 24, bottom: 56, left: 56 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#212121';
  ctx.font = '600 24px system-ui, sans-serif';
  ctx.fillText(title, pad.left, 32);

  if (!pts.length) return canvas.toDataURL('image/png');

  const sws = pts.map((p) => p.sw);
  const swMin = Math.min(...sws);
  const swMax = Math.max(...sws);
  const span = Math.max(swMax - swMin, 1e-9);
  const xAt = (sw: number) => pad.left + ((sw - swMin) / span) * plotW;
  const yAt = (kr: number) => pad.top + plotH * (1 - kr);

  ctx.strokeStyle = '#e0e0e0';
  for (let i = 0; i <= 5; i++) {
    const y = pad.top + (plotH * i) / 5;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + plotW, y);
    ctx.stroke();
  }
  ctx.strokeStyle = '#9e9e9e';
  ctx.strokeRect(pad.left, pad.top, plotW, plotH);

  const drawKr = (key: 'krw' | 'kro', color: string) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    let started = false;
    for (const p of pts) {
      const v = p[key];
      if (v == null || !Number.isFinite(v)) {
        started = false;
        continue;
      }
      const x = xAt(p.sw);
      const y = yAt(v);
      if (!started) {
        ctx.moveTo(x, y);
        started = true;
      } else ctx.lineTo(x, y);
    }
    ctx.stroke();
  };
  drawKr('krw', '#1565c0');
  drawKr('kro', '#c62828');

  ctx.fillStyle = '#455a64';
  ctx.font = '16px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  for (let i = 0; i <= 5; i++) {
    const sw = swMin + (span * i) / 5;
    ctx.fillText(fmtTick(sw), xAt(sw), pad.top + plotH + 8);
  }
  ctx.fillText('Sw', pad.left + plotW / 2, pad.top + plotH + 32);

  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (let i = 0; i <= 5; i++) {
    const v = (5 - i) / 5;
    ctx.fillText(fmtTick(v), pad.left - 8, pad.top + (plotH * i) / 5);
  }

  return canvas.toDataURL('image/png');
}

function fieldSeriesFromAgg(field: MbFieldSeriesPoint[]): {
  years: number[];
  series: Series[];
  rightMax: number;
} {
  const years = field.map((r) => r.year);
  const series: Series[] = [
    { key: 'oil', label: 'oil', color: FIELD_COLORS.oil, scale: 'left', values: field.map((r) => r.oil) },
    {
      key: 'liquid',
      label: 'liquid',
      color: FIELD_COLORS.liquid,
      scale: 'left',
      values: field.map((r) => r.liq),
    },
    {
      key: 'injection',
      label: 'injection',
      color: FIELD_COLORS.injection,
      scale: 'left',
      values: field.map((r) => r.inj),
    },
    {
      key: 'wc',
      label: 'water cut',
      color: FIELD_COLORS.waterCut,
      dash: [8, 6],
      scale: 'wc',
      values: field.map((r) => r.fw),
    },
    {
      key: 'np',
      label: 'Np',
      color: FIELD_COLORS.np,
      scale: 'right',
      values: field.map((r) => r.np),
    },
  ];
  return { years, series, rightMax: maxOf(field.map((r) => r.np)) };
}

function layerSeriesFromForecast(
  field: MaterialBalanceFieldPoint[],
  extractable: number | null,
): { years: number[]; series: Series[]; rightMax: number } {
  const years = field.map((r) => r.year);
  const series: Series[] = [
    { key: 'oil', label: 'oil', color: FIELD_COLORS.oil, scale: 'left', values: field.map((r) => r.oil) },
    {
      key: 'liquid',
      label: 'liquid',
      color: FIELD_COLORS.liquid,
      scale: 'left',
      values: field.map((r) => r.liq),
    },
    {
      key: 'injection',
      label: 'injection',
      color: FIELD_COLORS.injection,
      scale: 'left',
      values: field.map((r) => r.inj),
    },
    {
      key: 'wc',
      label: 'water cut',
      color: FIELD_COLORS.waterCut,
      dash: [8, 6],
      scale: 'wc',
      values: field.map((r) => r.fw ?? null),
    },
    {
      key: 'np',
      label: 'Np',
      color: FIELD_COLORS.np,
      scale: 'right',
      values: field.map((r) => r.np),
    },
  ];
  const rightMax = Math.max(maxOf(field.map((r) => r.np)), extractable ?? 0);
  return { years, series, rightMax };
}

class PdfDoc {
  pdf: jsPDF;
  y: number;

  constructor() {
    this.pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    this.y = MARGIN;
  }

  remaining(): number {
    return PAGE_H - MARGIN - this.y;
  }

  ensure(mm: number): void {
    if (this.remaining() < mm) this.newPage();
  }

  newPage(): void {
    this.pdf.addPage();
    this.y = MARGIN;
  }

  title(text: string, size = 16): void {
    this.ensure(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(size);
    this.pdf.setTextColor(33, 33, 33);
    this.pdf.text(text, MARGIN, this.y + size * 0.35);
    this.y += size * 0.55 + 4;
  }

  subtitle(text: string): void {
    this.ensure(8);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(97, 97, 97);
    const lines = this.pdf.splitTextToSize(text, CONTENT_W) as string[];
    this.pdf.text(lines, MARGIN, this.y);
    this.y += lines.length * 5 + 3;
  }

  body(text: string): void {
    this.ensure(8);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(33, 33, 33);
    const lines = this.pdf.splitTextToSize(text, CONTENT_W) as string[];
    this.pdf.text(lines, MARGIN, this.y);
    this.y += lines.length * 5 + 2;
  }

  kvTable(rows: PassportKv[], columns = 2): void {
    if (!rows.length) return;
    const colW = CONTENT_W / columns;
    const rowH = 7;
    this.pdf.setFontSize(8);
    for (let i = 0; i < rows.length; i += columns) {
      this.ensure(rowH + 1);
      for (let c = 0; c < columns; c++) {
        const row = rows[i + c];
        if (!row) continue;
        const x = MARGIN + c * colW;
        this.pdf.setFillColor(245, 245, 245);
        this.pdf.rect(x, this.y - 4, colW - 2, rowH, 'F');
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setTextColor(97, 97, 97);
        this.pdf.text(row.label, x + 1.5, this.y);
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(33, 33, 33);
        const val = row.unit ? `${row.value} ${row.unit}` : row.value;
        this.pdf.text(val, x + colW - 3.5, this.y, { align: 'right' });
      }
      this.y += rowH + 1.2;
    }
    this.y += 2;
  }

  simpleTable(headers: string[], body: string[][]): void {
    if (!body.length) {
      this.body('—');
      return;
    }
    const colW = CONTENT_W / headers.length;
    const rowH = 6.5;
    const drawHeader = () => {
      this.pdf.setFillColor(238, 238, 238);
      this.pdf.rect(MARGIN, this.y - 4, CONTENT_W, rowH, 'F');
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(33, 33, 33);
      headers.forEach((h, i) => {
        this.pdf.text(h, MARGIN + i * colW + 1.5, this.y);
      });
      this.y += rowH;
    };
    this.ensure(rowH * 2);
    drawHeader();
    this.pdf.setFont('helvetica', 'normal');
    for (const row of body) {
      if (this.remaining() < rowH + 1) {
        this.newPage();
        drawHeader();
        this.pdf.setFont('helvetica', 'normal');
      }
      row.forEach((cell, i) => {
        const t = this.pdf.splitTextToSize(cell, colW - 3) as string[];
        this.pdf.text(t[0] || '', MARGIN + i * colW + 1.5, this.y);
      });
      this.y += rowH;
    }
    this.y += 3;
  }

  /** Place a chart image; always starts on a fresh page if it would split. */
  chartImage(dataUrl: string, heightMm: number): void {
    if (!dataUrl) return;
    this.ensure(heightMm + 2);
    // If still not enough for the full chart, start a new page
    if (this.remaining() < heightMm) this.newPage();
    this.pdf.addImage(dataUrl, 'PNG', MARGIN, this.y, CONTENT_W, heightMm);
    this.y += heightMm + 6;
  }
}

export function buildProductionResultsPdfBlob(opts: {
  scenarioId: string;
  agg: MbFieldAggregate;
  passports: LayerPassportViewModel[];
}): Blob {
  const doc = new PdfDoc();
  const { scenarioId, agg, passports } = opts;

  doc.title('Production results', 18);
  doc.subtitle(`Scenario ${scenarioId}`);
  doc.subtitle(`Exported ${new Date().toISOString().replace('T', ' ').slice(0, 19)} UTC`);
  doc.body(
    `Field total from ${agg.layersWithResult} of ${agg.layerCount} layer(s) with applied material-balance results.`,
  );
  if (agg.extractableOilKt != null) {
    doc.body(`Field extractable oil: ${agg.extractableOilKt.toFixed(1)} kt`);
  }

  if (agg.field.length) {
    doc.newPage();
    doc.title('Field production');
    const { years, series, rightMax } = fieldSeriesFromAgg(agg.field);
    const img = renderLineChartPng({
      title: 'Field total',
      years,
      series,
      rightMax,
    });
    doc.chartImage(img, 105);

    doc.title('Active wells');
    const bars = renderBarsPng({
      title: 'Active wells',
      years: agg.field.map((r) => r.year),
      producers: agg.field.map((r) => r.n_prod),
      injectors: agg.field.map((r) => r.n_inj),
    });
    doc.chartImage(bars, 85);
  }

  for (const p of passports) {
    doc.newPage();
    doc.title(`Reservoir: ${p.layerName}`, 16);
    doc.subtitle(`Status: ${p.status}${p.hasResult ? '' : ' · no applied forecast yet'}`);

    doc.title('1. Summary', 12);
    doc.kvTable(p.summary, 2);

    doc.title('2. Reserves and reservoir properties', 12);
    doc.kvTable(p.propsRows, 2);
    if (p.diagnostics.length) {
      doc.subtitle('Simulation diagnostics');
      doc.kvTable(p.diagnostics, 2);
    }

    doc.title('3. Development system', 12);
    doc.kvTable(
      [
        { label: 'Producers', value: String(p.nProd) },
        { label: 'Injectors', value: String(p.nInj) },
        { label: 'Schedule span', value: p.scheduleSpan },
        { label: 'Injection', value: p.injectionNote },
      ],
      2,
    );
    doc.simpleTable(
      ['Well', 'Kind', 't0', 't1', 'x', 'y', 'q start'],
      p.wells.map((w) => [
        w.well_name,
        w.kind,
        w.t0 != null ? String(w.t0) : '—',
        w.t1 != null ? String(w.t1) : '—',
        w.x != null ? w.x.toFixed(2) : '—',
        w.y != null ? w.y.toFixed(2) : '—',
        w.q_start_td != null ? w.q_start_td.toFixed(2) : '—',
      ]),
    );

    doc.title('4. Relative permeability', 12);
    doc.kvTable(p.relpermSummary, 2);
    if (p.relpermChart.length) {
      const rp = renderRelpermPng(p.relpermChart, `Relperm · ${p.layerName}`);
      doc.chartImage(rp, 70);
    }

    doc.title('5. Forecast', 12);
    if (p.hasResult && p.forecast.length) {
      doc.kvTable(p.forecastKpis, 2);
      // Always put the forecast chart on a clean page so axes never split
      doc.newPage();
      doc.title(`Forecast · ${p.layerName}`, 14);
      const { years, series, rightMax } = layerSeriesFromForecast(
        p.forecast,
        p.extractableOilKt,
      );
      const img = renderLineChartPng({
        title: 'Reservoir total',
        years,
        series,
        rightMax,
      });
      doc.chartImage(img, 110);
    } else {
      doc.body('No applied result yet.');
    }
  }

  return doc.pdf.output('blob');
}
